/**
 * PhotoThumb — fetches a presigned thumbnail URL and renders it.
 *
 * Sprint L. We deliberately don't render `<img src={photo.url}>` from
 * the listing DTO because:
 *   - photo URLs in MinIO are private; only the API can sign them.
 *   - the thumb (200×200 JPEG) is a separate object; the listing DTO
 *     only knows the cover photo's *id*.
 *
 * Rendering strategy: one fetch per photo_id on mount. The presigned
 * URL has a 5-minute TTL — far longer than a user's scroll session, so
 * we don't re-fetch on remounts within the same view. If we ever need
 * batched grid renders we'll add an in-memory cache keyed by photo_id.
 *
 * Failure mode is graceful: a tiny placeholder square. Photos that
 * haven't been staged yet return 409 — we show the placeholder, not an
 * error block, because the row is otherwise valid.
 */

import { useEffect, useState } from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@vseh/ui";

import { photosApi, ApiError } from "../api";


interface Props {
  photoId:   string | null;
  size?:     number;     // pixel edge; default 32 (table-row size)
  rounded?:  boolean;    // rounded corners for grid layouts
  alt?:      string;
  variant?:  "thumb" | "full";
}


export function PhotoThumb({
  photoId, size = 32, rounded = false, alt = "", variant = "thumb",
}: Props): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!photoId) return;
    let alive = true;
    photosApi.getUrl(photoId, variant)
      .then((r) => { if (alive) setUrl(r.url); })
      .catch((e) => {
        if (!alive) return;
        // 404/409 → placeholder; anything else → still placeholder
        // (we don't want a broken thumbnail to dominate the table row).
        if (e instanceof ApiError) setFailed(true);
        else setFailed(true);
      });
    return () => { alive = false; };
  }, [photoId, variant]);

  const placeholder = !photoId || failed || !url;
  const box = cn(
    "flex shrink-0 items-center justify-center overflow-hidden bg-base-100",
    rounded ? "rounded-md" : "rounded-sm",
  );

  if (placeholder) {
    return (
      <div className={box} style={{ width: size, height: size }}>
        <ImageOff
          className="text-base-300"
          style={{ width: size * 0.5, height: size * 0.5 }}
          aria-label="no photo"
        />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      width={size}
      height={size}
      loading="lazy"
      className={cn(
        box, "object-cover",
      )}
      style={{ width: size, height: size }}
    />
  );
}
